import { onSnapshot, query, collection, orderBy, where } from 'firebase/firestore';
import { db } from '../../../firebase';
import { logger } from '../../../shared/utils/logger';
import { limit as firestoreLimit } from 'firebase/firestore';
import { mapFirestorePostToUIPost } from '../../../shared/utils/posts/mapFirestorePostToUIPost';

/**
 * Use case pour s'abonner en temps réel aux activités
 * Sert de pont entre l'infrastructure Firebase et le domaine
 */
export default class SubscribeToActivities {
  constructor(postRepository) {
    this.postRepository = postRepository;
  }

  /**
   * Établit un abonnement temps réel aux activités
   * @param {Object} options 
   * @param {number} options.limit - Nombre maximum d'activités à récupérer
   * @param {Function} options.onUpdate - Callback appelé quand les données sont mises à jour
   * @param {Function} options.onError - Callback appelé en cas d'erreur
   * @returns {Object} - Fonctions pour gérer l'abonnement
   */
  async execute({ limit = 10, onUpdate, onError }) {
    try {
      // Filtrer par date future (activités à venir)
      const now = new Date();
      
      const activitiesQuery = query(
        collection(db, 'posts'),
        where('activity.date', '>', now),
        orderBy('activity.date', 'asc'),
        firestoreLimit(limit)
      );

      let initialPosts = [];
      
      // Fonction pour définir les posts initiaux
      const setInitialPosts = (setter) => {
        if (initialPosts.length > 0) {
          setter(initialPosts);
        }
      };
      
      // Établir l'abonnement temps réel
      const unsubscribe = onSnapshot(
        activitiesQuery,
        (snapshot) => {
          const changes = snapshot.docChanges();
          
          // Gérer les changements incrémentaux
          initialPosts = this._processChanges(changes, initialPosts);
          
          // Notifier les abonnés
          const lastVisibleDoc = snapshot.docs.length > 0 
            ? snapshot.docs[snapshot.docs.length - 1] 
            : null;
            
          const hasMore = snapshot.docs.length === limit;
          
          onUpdate(initialPosts, lastVisibleDoc, hasMore);
        },
        (err) => {
          logger.error('Error in subscription to activities:', err);
          onError(err);
        }
      );

      return { 
        unsubscribe,
        setInitialPosts
      };
    } catch (error) {
      logger.error('Error setting up activities subscription:', error);
      onError(error);
      return { 
        unsubscribe: () => {}, 
        setInitialPosts: () => {} 
      };
    }
  }

  /**
   * Transforme les changements Firestore en mises à jour du modèle de domaine
   * @private
   */
  _processChanges(changes, currentPosts) {
    let updatedPosts = [...currentPosts];
    
    changes.forEach(change => {
      const doc = change.doc;
      const data = doc.data();

      // Utiliser la fonction de mapping centralisée
      const post = mapFirestorePostToUIPost(data, doc.id);

      if (change.type === 'added') {
        const existingIndex = updatedPosts.findIndex(p => p.id === post.id);
        if (existingIndex === -1) {
          // Insérer au bon endroit pour maintenir le tri par date
          const insertIndex = updatedPosts.findIndex(p => 
            p.activity?.date > post.activity?.date
          );
          
          if (insertIndex === -1) {
            updatedPosts.push(post);
          } else {
            updatedPosts.splice(insertIndex, 0, post);
          }
        }
      } else if (change.type === 'modified') {
        const index = updatedPosts.findIndex(p => p.id === post.id);
        if (index !== -1) {
          updatedPosts[index] = post;
        }
      } else if (change.type === 'removed') {
        updatedPosts = updatedPosts.filter(p => p.id !== post.id);
      }
    });

    return updatedPosts;
  }
} 